import React, { useState, useCallback, Suspense, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { CircularProgress, Box } from "@mui/material";
import { history } from "utils/_helpers/history";
import axios from "utils/axios/axios";
import {
  logOut,
  setLogOut,
  setLogin,
  setToken,
} from "../../actions/login/loginActions";
import { set_snack_bar } from "../../actions/snackbar/snackbar_action";
import {
  setRedirectValue,
  getNotifications,
  setNotification,
  setTotalNotification,
} from "../../actions/surveyListing/surveyListingAction";
import {
  getToken,
  getAuthData,
  setPostLoginRedirect,
  clearAuthData,
  cleanupOldAuthData,
} from "../../utils/encryption";
import {
  setUserDetails,
  checkAndRefreshTokenIfNeeded,
  syncOrgDetails,
} from "../../actions/auth/authAction";

// Containers
import SnackbarCont from "../../containers/snackbar/snackbarCont";
import LoaderCon from "../../containers/loader/loader_cont";

// Components
import ErrorBoundary from "./ErrorBoundary";
import DesktopNotificationClearButton from "../UI/DesktopNotificationClearButton/DesktopNotificationClearButton";

// Layouts
import Layout from "../layout/AuthRouteLayout";

// Route components
import UnauthenticatedRoutes from "./routes/UnauthenticatedRoutes";
const AuthenticatedRoutes = React.lazy(
  () => import("./routes/AuthenticatedRoutes")
);
const SignupProcessRoutes = React.lazy(
  () => import("./routes/SignupProcessRoutes")
);

// Simple loading spinner
const LoadingSpinner = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      backgroundColor: "#f5f5f5",
    }}
  >
    <CircularProgress />
  </Box>
);

/**
 * HistoryInitializer - Initializes the history object for use outside React components
 */
const HistoryInitializer = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    history.navigate = navigate;
    history.location = location;
  }, [navigate, location]);

  return <>{children}</>;
};

/**
 * Simple useEffect-based auth initialization
 * Reads encrypted localStorage and restores Redux state on mount
 */
const useAuthInit = () => {
  const dispatch = useDispatch();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const authData = getAuthData();

    if (authData.token && authData.user) {
      // Restore Redux state from encrypted localStorage
      dispatch(setUserDetails({ ...authData.user, isLoggedin: true }));
      dispatch(setLogin(authData.user));
      dispatch(setToken(authData.token));
      axios.defaults.headers.common.Authorization = `Bearer ${authData.token}`;

      // Check token expiry and refresh if needed (only if expired)
      dispatch(checkAndRefreshTokenIfNeeded()).finally(() => {
        // Heal org_details drift (another admin may have edited the org's
        // sector/industry since this user logged in). Fire-and-forget — boot
        // is not delayed; consumers like the materiality Recommended filter
        // refetch when the merged industry_id lands in redux.
        if (getToken()) {
          dispatch(syncOrgDetails());
        }
        setIsReady(true);
      });

      cleanupOldAuthData();
    } else {
      dispatch(setLogOut());
      setIsReady(true);
    }
  }, [dispatch]);

  return isReady;
};

/**
 * AppRouterContent - Inner router content with hooks
 */
const AppRouterContent = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();

  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS

  // Simple auth initialization from localStorage
  const isAuthReady = useAuthInit();

  // Auth state from Redux
  const user = useSelector((state) => state.user);
  const login = useSelector((state) => state.login);
  const snackbar = useSelector((state) => state.snackbar);
  const surveyListing = useSelector((state) => state.surveyListing);

  const isLoggedin = user.isLoggedin;
  const signupProcess = user.signup_process;
  const userType = user.user_type;

  // Store current path for post-login redirect
  useEffect(() => {
    const token = getToken();
    const currentPath = location.pathname + location.search;
    const publicPaths = ["/login", "/signup", "/verify", "/forgot_password"];

    if (
      !token &&
      !publicPaths.some((path) => currentPath.startsWith(path)) &&
      currentPath !== "/"
    ) {
      setPostLoginRedirect(currentPath);
    }
  }, [location]);

  // Auto-logout on 401/Invalid user response
  useEffect(() => {
    if (snackbar.response_received && snackbar.message === "Invalid user") {
      clearAuthData();
      dispatch(setLogOut());
      history.navigate?.("/login");
    }
  }, [snackbar, dispatch]);

  const toggleSidebar = useCallback((isOpen) => {
    setIsOpen(isOpen);
  }, []);

  // Combined login props from user and login reducers
  const loginProps = {
    ...login,
    token: getToken() || login.token || "",
    user_type: user.user_type || login.user_type || "",
    name: user.name || login.name || "",
    email: user.email || login.email || "",
    isLoggedin: user.isLoggedin,
    current_organisation:
      user.current_organisation || login.current_organisation || "",
    currentOrganisationUserType:
      user.currentOrganisationUserType ||
      login.currentOrganisationUserType ||
      "",
  };

  // Essential props for Layout (removed getOrganizations - data is in user object)
  const layoutProps = {
    logOut: (payload) => dispatch(logOut(payload)),
    setLogOut: () => dispatch(setLogOut()),
    set_snack_bar: (status, response) =>
      dispatch(set_snack_bar(status, response)),
    getNotifications: (token, organisation_id, limit, starting_after) =>
      dispatch(getNotifications(token, organisation_id, limit, starting_after)),
    setNotification: (payload) => dispatch(setNotification(payload)),
    setTotalNotification: (payload) => dispatch(setTotalNotification(payload)),
    setRedirectValue: () => dispatch(setRedirectValue()),
  };

  // Show loading only during initial auth check (after all hooks)
  if (!isAuthReady) {
    return <LoadingSpinner />;
  }

  return (
    <div style={{ overflow: "hidden" }}>
      <SnackbarCont />
      <LoaderCon />
      <DesktopNotificationClearButton />

      <ErrorBoundary>
      <Routes>
        <Route
          path="/*"
          element={
            !isLoggedin ? (
              <UnauthenticatedRoutes />
            ) : signupProcess === 2 || signupProcess === 3 ? (
              <Suspense fallback={<LoadingSpinner />}>
                <SignupProcessRoutes />
              </Suspense>
            ) : location.pathname === "/add_new_company" ? (
              <Suspense fallback={<LoadingSpinner />}>
                <AuthenticatedRoutes userType={userType} />
              </Suspense>
            ) : (
              <Layout
                isOpen={isOpen}
                setIsOpen={toggleSidebar}
                login={loginProps}
                surveyListing={surveyListing}
                {...layoutProps}
                {...props}
              >
                <Suspense fallback={<LoadingSpinner />}>
                  <AuthenticatedRoutes userType={userType} />
                </Suspense>
              </Layout>
            )
          }
        />
      </Routes>
      </ErrorBoundary>
    </div>
  );
};

/**
 * AppRouter - Simplified routing with encrypted localStorage auth
 */
const AppRouter = (props) => {
  return (
    <BrowserRouter>
      <HistoryInitializer>
        <AppRouterContent {...props} />
      </HistoryInitializer>
    </BrowserRouter>
  );
};

export default AppRouter;
